/** @jsx jsx */
import { jsx, ThemeUIStyleObject, ResponsiveStyleValue } from "theme-ui";
import React, { Fragment } from "react";

import { Card, CardLink, getImageWidthForCard } from "../Card";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { Text } from "@shared-components/Text/Text";
import { AspectRatio } from "@shared-components/AspectRatio/AspectRatio";

import {
  CardInnerStyles,
  CardSplashStyles,
  CardSplashBottomStyles,
  CardStyles,
  CardRedbgStyles,
  ThreeLeafStyles,
  ThreeLeafRedStyles,
  CardAspectRatioGradientStyles,
  CardAspectRatioStyles,
} from "../styles";

import { PrimitiveProps } from "@styles/types";
import ParseHtmlToReact from "@shared-components/ParseHtmlToReact/ParseHtmlToReact";
import { UmbracoBackgroundImage } from "@shared-components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage";
import { ImageCardContentElement } from "src/carlsberg-headless/types/content-elements/ImageCard";

import ThreeLeafCloverSvg from "@svg-icons/three-leaf-clover.component.svg";
import { Icon } from "@shared-components/Icon/Icon";
import { THEMES_MAP } from "../../../../carlsberg-headless/themes-map";

export interface SplashProps extends PrimitiveProps {
  text: string;
  variant?: string;
}
const CardSplash: React.FC<SplashProps> = ({ text, variant }) => (
  <Box
    sx={{
      ...CardSplashStyles,
      ...(variant === "cancelled" && CardSplashBottomStyles),
    }}
    variant={`cardSplash.${variant}`}
  >
    {text && <ParseHtmlToReact html={text} />}
  </Box>
);

const BoxContainer: React.FC<ImageCardProps> = ({ ...props }) => {
  return (
    <Fragment>
      {props.calloutTextTop && (
        <CardSplash text={props.calloutTextTop} variant={"eventcard_custom"} />
      )}
      <Box
        sx={
          props.image1 ? CardAspectRatioGradientStyles : CardAspectRatioStyles
        }
      >
        <Box
          sx={{
            ...CardInnerStyles,
          }}
        >
          {props.section && (
            <Text as="p" variant="cardSectionText" sx={{ mt: 2 }}>
              {props.section}
            </Text>
          )}
          {props.title && (
            <Heading as="h2" variant={"h3"} sx={{ mt: 2 }}>
              {props.title}
            </Heading>
          )}

          {props.calloutTextBottom && (
            <Box sx={{ mb: 0 }}>
              <ParseHtmlToReact html={props.calloutTextBottom} />
            </Box>
          )}
        </Box>
      </Box>
    </Fragment>
  );
};

const AspectRatioBox: React.FC<ImageCardProps> = ({ ...props }) => {
  const hasRedBackground = props.themes.includes(
    THEMES_MAP.cards_module.card.cards_module_red_background
  );

  const imageWidth =
    props.ratio &&
    (getImageWidthForCard(props.ratio) as ResponsiveStyleValue<
      string | number
    >);

  return (
    <AspectRatio ratio={props.ratio} sx={{ height: "100%" }}>
      <Icon
        sx={{
          ...ThreeLeafStyles,
          ...(hasRedBackground && ThreeLeafRedStyles),
        }}
        svg={ThreeLeafCloverSvg}
      />
      {props.image1 ? (
        <UmbracoBackgroundImage
          image={props.image1}
          {...(imageWidth ? { width: imageWidth } : { width: [340, 700] })}
        >
          <BoxContainer {...props} />
        </UmbracoBackgroundImage>
      ) : (
        <BoxContainer {...props} />
      )}
    </AspectRatio>
  );
};

export interface ImageCardProps extends ImageCardContentElement {
  ratio: string | string[];
  sx: ThemeUIStyleObject;
}

const ImageCard: React.FC<ImageCardProps> = ({ ...props }) => {
  const hasRedBackground = props.themes.includes(
    THEMES_MAP.cards_module.card.cards_module_red_background
  );

  return (
    <Card
      key={props.cbKey}
      sx={{
        ...CardStyles,
        ...(hasRedBackground && CardRedbgStyles),
        ...props.sx,
      }}
    >
      {props.link ? (
        <CardLink {...props.link}>
          <AspectRatioBox {...props} />
        </CardLink>
      ) : (
        <AspectRatioBox {...props} />
      )}
    </Card>
  );
};

ImageCard.defaultProps = {};

export { ImageCard };
